import { Portfolio } from '@blk/aw-bff-typescript-models';
import { Origin } from '@blk/awt-aladdinwealthserver';
import { AxiosResponse, CancelToken } from 'axios';
import { AGraphWrappedType, unwrapAGraphResponse } from '../../utils/AGraphWrapper';
import {
  distributeOpenLotsToPositions,
  prefixEnumForAGraph,
  prefixEnumsForAGraph,
  transformTimestamps,
} from '../../utils/ApiUtils';
import { PortfolioTag, SearchRequest, SearchResponse } from '../AWBridge';
import {
  AGRAPH_PORTFOLIO_ENUM_PREFIXES,
  AGRAPH_PORTFOLIO_POST_TIMESTAMP_FIELDS,
  AGRAPH_SEARCH_REQUEST_ENUM_PREFIXES,
} from './AGraphConstants';
import { isAGraphEnabled } from './AGraphUtils';
import { awPortfolioApi } from './Base';
import { AW_PORTFOLIO_TAGS } from './Constants';

interface GetPortfolioParams {
  allowMissing?: boolean;
  asOf?: string; // analysis date in RFC3339 (ISO-8601) format
  origin: Origin;
  pkg?: string;
}

interface AGraphGetPortfolioParams extends Omit<GetPortfolioParams, 'origin'> {
  origin: string;
}

interface BulkGetPortfolioBodyParams {
  ids: string[];
}

export function getPortfolio(
  tag: AW_PORTFOLIO_TAGS,
  aggIDValue: string,
  params: GetPortfolioParams,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Portfolio>> {
  const path = `portfolios/${prefixEnumForAGraph(tag, 'PORTFOLIO_TAG_', isAGraphEnabled())}/${aggIDValue}`;
  if (isAGraphEnabled()) {
    (params as AGraphGetPortfolioParams).origin = `${prefixEnumForAGraph(params.origin, 'ORIGIN_', true)}`;
  }
  return awPortfolioApi.get<Portfolio>(path, { params, cancelToken });
}
export async function postBulkGetPortfolios(
  tag: AW_PORTFOLIO_TAGS,
  origin: Origin,
  params: BulkGetPortfolioBodyParams,
  asOf?: string,
  benchmarkType?: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Portfolio[]>> {
  const originParam = prefixEnumForAGraph(origin, 'ORIGIN_', isAGraphEnabled());
  const tagParam = prefixEnumForAGraph(tag, 'PORTFOLIO_TAG_', isAGraphEnabled());
  const path = `portfolios/bulkGet?origin=${originParam}&tag=${tagParam}${asOf ? `&asOf=${asOf}` : ''}${benchmarkType ? `&benchmarkType=${benchmarkType}` : ''}`;
  const response = await awPortfolioApi.post<AGraphWrappedType<Portfolio[], 'results'>>(path, params, { cancelToken });
  return unwrapAGraphResponse(response, 'results');
}

export function postCreatePortfolio(portfolio: Portfolio, pkg?: string) {
  const path = 'portfolios';
  let payload = portfolio;
  if (isAGraphEnabled()) {
    payload = transformTimestamps(portfolio, AGRAPH_PORTFOLIO_POST_TIMESTAMP_FIELDS, 'stringify', {
      recursiveFieldPatterns: ['*'],
    });
    payload = prefixEnumsForAGraph(payload, AGRAPH_PORTFOLIO_ENUM_PREFIXES);
    payload = distributeOpenLotsToPositions(payload);
  }
  return awPortfolioApi.post<Portfolio>(path, payload, { params: { pkg } });
}

const AGRAPH_REQUEST_ENUM_PREFIXES = [
  {
    fieldNames: ['origin'],
    prefix: 'ORIGIN_',
    recursiveFieldPatterns: ['portfolios', 'benchmark', 'otherBenchmarks', 'model', 'otherModels'],
  },
];

export async function postBulkCreatePortfolio(portfolios: Portfolio[]) {
  let path = 'portfolios/bulkCreate';
  let payload = { portfolios };

  if (isAGraphEnabled()) {
    path = 'portfolios:bulkCreate';
    payload = prefixEnumsForAGraph(payload, AGRAPH_REQUEST_ENUM_PREFIXES);
    payload = distributeOpenLotsToPositions(payload, { recursiveFieldPatterns: ['portfolios'] });
  }
  const response = await awPortfolioApi.post<AGraphWrappedType<Portfolio[], 'results'>>(path, payload);
  return unwrapAGraphResponse(response, 'results');
}

export function putUpdatePortfolio(tag: AW_PORTFOLIO_TAGS, id: string, portfolio: Portfolio, pkg: string) {
  if (isAGraphEnabled()) {
    // The PUT endpoint was not migrated to AGraph initially - but the POST does the same thing.
    return postCreatePortfolio(portfolio, pkg);
  }
  const path = `portfolios/${tag}/${id}`;
  return awPortfolioApi.put<Portfolio>(path, portfolio, { params: { pkg } });
}

export function deletePortfolio(tag: AW_PORTFOLIO_TAGS, id: string, origin: Origin, pkg?: string) {
  if (isAGraphEnabled()) {
    // The DELETE endpoint was not migrated to AGraph initially - so we can pass the params to bulkDelete for now.
    return postBulkDeletePortfolio([id], tag, origin, pkg);
  }
  const path = `portfolios/${tag}/${id}?origin=${origin}`;
  return awPortfolioApi.delete<string>(path, { params: { pkg } });
}

export function postSearchPortfolios(
  origin: Origin,
  params: SearchRequest,
  pkg: string,
  tag?: PortfolioTag,
  cancelToken?: CancelToken
): Promise<AxiosResponse<SearchResponse<Portfolio>>> {
  /* As per latest, swagger documents, tag is supposed to be passed as a query params. */
  const prefixedOrigin = prefixEnumForAGraph(origin, 'ORIGIN_', isAGraphEnabled());
  const prefixedTag = tag ? prefixEnumForAGraph(tag, 'PORTFOLIO_TAG_', isAGraphEnabled()) : '';

  const path = `portfolios/search?origin=${prefixedOrigin}${prefixedTag ? `&tag=${prefixedTag}` : ''}`;
  let payload = params;
  if (isAGraphEnabled()) {
    payload = prefixEnumsForAGraph(payload, AGRAPH_SEARCH_REQUEST_ENUM_PREFIXES);
    payload = transformTimestamps(payload, ['limit'], 'stringify', {
      recursiveFieldPatterns: ['*'],
    });
  }
  return awPortfolioApi.post(path, payload, { cancelToken, params: { pkg } });
}

export function postSendPortfolio(tag: AW_PORTFOLIO_TAGS, id: string, origin: Origin, users: string[], pkg?: string) {
  const csvUsers = users.join(',');
  const path = `portfolios/${tag}/${id}/send?origin=${origin}&users=${csvUsers}`;
  return awPortfolioApi.post(path, isAGraphEnabled() ? {} : undefined, { params: { pkg } });
}

export function postSharePortfolio(tag: AW_PORTFOLIO_TAGS, id: string, origin: Origin, users: string[]) {
  const csvUsers = users.join(',');
  const path = `portfolios/${tag}/${id}/share?origin=${origin}&users=${csvUsers}`;
  return awPortfolioApi.post(path, isAGraphEnabled() ? {} : undefined);
}

export function postBulkDeletePortfolio(ids: string[], tag: AW_PORTFOLIO_TAGS, origin: Origin, pkg?: string) {
  const path = 'portfolios/bulkDelete';
  const prefixedOrigin = prefixEnumForAGraph(origin, 'ORIGIN_', isAGraphEnabled());
  const prefixedTag = prefixEnumForAGraph(tag, 'PORTFOLIO_TAG_', isAGraphEnabled());
  return awPortfolioApi.post<string>(path, { ids }, { params: { pkg, origin: prefixedOrigin, tag: prefixedTag } });
}

export async function getStaticBenchmarks(asOf: string, pkg: string): Promise<AxiosResponse<Portfolio[]>> {
  const path = 'portfolios/static-benchmarks';
  const params = { asOf, pkg };
  const response = await awPortfolioApi.get<AGraphWrappedType<Portfolio[], 'portfolios'>>(path, { params });
  return unwrapAGraphResponse(response, 'portfolios');
}

export async function getStaticModels(asOf: string, pkg: string): Promise<AxiosResponse<Portfolio[]>> {
  const path = 'portfolios/static-models';
  const params = { asOf, pkg };
  const response = await awPortfolioApi.get<AGraphWrappedType<Portfolio[], 'portfolios'>>(path, { params });
  return unwrapAGraphResponse(response, 'portfolios');
}

export function getUserPortfolios(
  tag: AW_PORTFOLIO_TAGS,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Portfolio[]>> {
  const path = `portfolios/user/${tag}`;
  return awPortfolioApi.get<Portfolio[]>(path, { cancelToken });
}

interface GetAllPortfoliosByPageCursorRequest {
  cancelToken?: CancelToken;
  curPortfolios: Portfolio[];
  cursor?: string;
  pkg: string;
  tag: AW_PORTFOLIO_TAGS;
}

type PortfoliosByCursorResp = {
  items: Portfolio[];
  cursor: string;
};

const getPortfolioByPageCursor = (tag: AW_PORTFOLIO_TAGS, cursor?: string, cancelToken?: CancelToken, pkg?: string) => {
  const path = `portfolios/user-paginate/${prefixEnumForAGraph(tag, 'PORTFOLIO_TAG_', isAGraphEnabled())}`;
  return awPortfolioApi.get<PortfoliosByCursorResp>(path, { params: { cursor, pkg }, cancelToken });
};

export const getAllPortfoliosByPageCursor = async ({
  curPortfolios,
  tag,
  cursor,
  cancelToken,
  pkg,
}: GetAllPortfoliosByPageCursorRequest): Promise<Portfolio[]> => {
  const {
    data: { items: portfolios, cursor: nextCursor },
  } = await getPortfolioByPageCursor(tag, cursor, cancelToken, pkg);
  if (portfolios) {
    const newPortfolios = curPortfolios.concat(portfolios);
    if (nextCursor) {
      return getAllPortfoliosByPageCursor({ curPortfolios: newPortfolios, tag, cursor: nextCursor, cancelToken, pkg });
    }
    return newPortfolios;
  }
  return curPortfolios;
};
